/**
 * web/src/games/signals.ts — "Waiting for you" on a Games tile (card jT0odGfG7MVo).
 *
 * The badge is not a new signal: each game already records its own open
 * round/match on the server, so this asks the five existing routes once and
 * reads, per game, "the partner has acted and the next move is mine". A route
 * that fails or aborts simply leaves its tile without a badge — a badge is
 * decoration, never a gate.
 *
 * Nocturne is absent on purpose: its state lives in the couple room's
 * transient `nocturne` pipe, never on the server, so there is nothing to ask
 * for at list time.
 */
import { useEffect, useState } from 'react';
import { api, session } from '../shell/bridge';
import type { GameId } from './catalogue';

export type GameWaiting = Partial<Record<GameId, boolean>>;

/**
 * Two readings of the same five routes (card aduRfcsTSaXW):
 *   `waiting` — the partner has acted and the next move is mine (the badge);
 *   `started` — any live session exists, whoever it is waiting on. A started
 *               game is not listed as a section tile; it surfaces in Pear
 *               recommends instead, so a half-played game never looks fresh.
 */
export interface GameSignals {
  waiting: GameWaiting;
  started: GameWaiting;
}

interface RoundLite {
  state: string;
  /** Back and forth, This or that and Would you rather share this route. */
  category?: string;
  answeredBy?: string[];
  myAnswer?: string | null;
  myPassed?: boolean;
  options?: [string, string] | null;
}

interface GuessMeLite {
  state: string;
  myRole: 'answerer' | 'guesser';
  myAnswer: string | null;
  myGuess: string | null;
  myLocked: boolean;
  partnerAnswer: string | null;
}

interface GuessmateLite {
  state: string;
  myTurn: boolean;
}

interface HalfAMapLite {
  state: string;
  current: { canMove: boolean; canReveal: boolean } | null;
}

interface WhereAmILite {
  state: string;
  guesses: { memberId: string; text: string }[];
}

async function get<T>(path: string): Promise<T | null> {
  try {
    return await api<T>(path);
  } catch {
    return null;
  }
}

/**
 * Reads every game's open session once. `enabled` is false while a game is
 * open, so the list stops polling nothing.
 */
export function useGameSignals(enabled = true): GameSignals {
  const me = session();
  const memberId = me?.memberId ?? null;
  const [signals, setSignals] = useState<GameSignals>({ waiting: {}, started: {} });

  useEffect(() => {
    if (!enabled || !memberId) return;
    let live = true;
    void (async () => {
      const [round, guessMe, guessmate, halfAMap, whereAmI] = await Promise.all([
        get<{ round: RoundLite | null }>('/api/game/round'),
        get<{ round: GuessMeLite | null }>('/api/game/guess-me/round'),
        get<{ match: GuessmateLite | null }>('/api/game/guessmate/match'),
        get<{ match: HalfAMapLite | null }>('/api/game/half-a-map/match'),
        get<{ round: WhereAmILite | null }>('/api/game/where-am-i/round'),
      ]);
      if (!live) return;

      const partnerAnswered =
        !!round?.round?.answeredBy?.some(id => id !== memberId);
      const next: GameWaiting = {
        /* Back and forth: the partner has written, you have not. */
        'back-and-forth':
          !!round?.round &&
          round.round.state === 'open' &&
          partnerAnswered &&
          !round.round.myAnswer &&
          !round.round.myPassed,
        /* Guess Me: the guesser has choices waiting, or the answerer owes a line. */
        'guess-me':
          !!guessMe?.round &&
          guessMe.round.state === 'open' &&
          (guessMe.round.myRole === 'answerer'
            ? !guessMe.round.myAnswer
            : !guessMe.round.myGuess && !!guessMe.round.partnerAnswer),
        /* Guessmate: the match is live and the clock is on your seat. */
        guessmate: !!guessmate?.match && guessmate.match.state === 'playing' && guessmate.match.myTurn,
        /* Half a Map: your role has a move or a reveal left this round. */
        'half-a-map':
          !!halfAMap?.match &&
          halfAMap.match.state === 'playing' &&
          !!halfAMap.match.current &&
          (halfAMap.match.current.canMove || halfAMap.match.current.canReveal),
        /* Where Am I: the partner has guessed and you have not. */
        'where-am-i':
          !!whereAmI?.round &&
          whereAmI.round.state === 'open' &&
          whereAmI.round.guesses.some(g => g.memberId !== memberId) &&
          !whereAmI.round.guesses.some(g => g.memberId === memberId),
      };
      /* Which games have a live session at all (card aduRfcsTSaXW). This is
         "started", not "my turn": a game the partner opened and is still
         sitting on is started too, and it belongs in Pear recommends rather
         than in the section list. This-or-that and Would you rather share
         back-and-forth's round route, so the round's own category decides
         which tile counts as started. */
      /* Only a round still being played counts as started: a revealed round is
         finished history (it may even carry the discard that must keep the
         game off the tab), so it is not "in progress". */
      const cat = round?.round && round.round.state === 'open' ? round.round.category ?? '' : '';
      const quizGame: GameId =
        cat === 'this-or-that'
          ? 'this-or-that'
          : cat === 'would-you-rather' || cat.startsWith('wyr-')
            ? 'would-you-rather'
            : 'back-and-forth';
      const startedOk: GameWaiting = {
        ...(cat ? { [quizGame]: true } : {}),
        ...(guessMe?.round && guessMe.round.state !== 'revealed' ? { 'guess-me': true } : {}),
        ...(guessmate?.match && guessmate.match.state === 'playing' ? { guessmate: true } : {}),
        ...(halfAMap?.match && halfAMap.match.state === 'playing' ? { 'half-a-map': true } : {}),
        ...(whereAmI?.round && whereAmI.round.state === 'open' ? { 'where-am-i': true } : {}),
      };

      setSignals({ waiting: next, started: startedOk });
    })();
    return () => {
      live = false;
    };
  }, [enabled, memberId]);

  return signals;
}
